/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 * See the LICENSE file for the full text.
 */

/**
 * One row of the statement.
 *
 * Collapsed it answers "what, when, how much, did it work". Expanded it shows
 * the breakdown and offers to write to us about this payment — which opens the
 * contact form already filled in, rather than handing the reader to whatever
 * mail client their machine may or may not have.
 *
 * THE MONEY NEVER WAITS FOR THE NAME. Amount, status, date and order id come
 * from the one scoped read and are drawn immediately; only the title line can
 * shimmer, and only until the optional name read settles one way or the other.
 */

import React from 'react';
import { Icon, Skeleton } from '../../../components/feed/FeedParts';
import { rowTitle } from '../../../components/athlohive/myPayments';

const TONE_ICON = {
  ok: 'check_circle',
  no: 'error',
  wait: 'schedule',
  ref: 'undo',
};

/*
 * THE WORDS OF THE SUPPORT MESSAGE LIVE HERE, IN ONE PLACE.
 *
 * They are written into the contact form by PaymentSupportModal, and they used
 * to go into a mailto: on this card. Two copies of this text would drift the
 * first time somebody reworded one of them, so both callers read these.
 *
 * `row` is optional: the page's footer offers the same channel without naming
 * a payment, and a general query simply has no detail block.
 */

/** The subject line, which the backend uses as the email's own subject. */
export function supportSubject(row) {
  return row ? `Payment query — order ${row.orderId}` : 'Payment query';
}

/** The message body, with everything we would otherwise have to ask for. */
export function supportBody(row) {
  if (!row) {
    return [
      'Hello,',
      '',
      'I would like to raise a query regarding a payment.',
      '',
      'Details of my query:',
      '',
    ].join('\n');
  }
  return [
    'Hello,',
    '',
    'I would like to raise a query regarding the following payment:',
    '',
    `  Order ID: ${row.orderId}`,
    `  Amount:   ${row.total}`,
    `  Date:     ${row.stamp}`,
    `  Status:   ${row.statusLabel}`,
    '',
    'Details of my query:',
    '',
  ].join('\n');
}

/*
 * The mailto: this card offered before the modal existed. KEPT, COMMENTED, and
 * still built from the two helpers above, so restoring the old behaviour is
 * uncommenting this and the <a> at the foot of the expanded row.
 *
 * It needed REACT_APP_SUPPORT_EMAIL, which is now commented out of the
 * environment too: a payment query goes to the addresses the backend already
 * notifies for the contact form (FORM_NOTIFY_EMAILS), so the web app no longer
 * holds a mailbox of its own.
 */
// export function mailtoFor(row, supportEmail) {
//   if (!supportEmail) return '';
//   const subject = supportSubject(row);
//   const body = supportBody(row);
//   return `mailto:${supportEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
// }

export default function PaymentCard({
  row, naming, regActivities, expanded, onToggle, onSupport,
}) {
  const { title, activity, pending } = rowTitle(row, naming, regActivities);
  // const mailto = mailtoFor(row, supportEmail);

  return (
    <div className={`fd-pay${expanded ? ' open' : ''}`}>
      <button
        type="button"
        className="fd-pay-hd"
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className={`fd-pay-ico ${row.tone}`}>
          <Icon name={TONE_ICON[row.tone] || 'receipt_long'} />
        </span>

        <span className="fd-pay-mid">
          <span className="fd-pay-ev">
            {pending ? (
              <Skeleton height={15} radius={6} style={{ width: '58%', maxWidth: 220 }} />
            ) : (
              <>
                {title}
                {activity && <span className="fd-pay-ac"> — {activity}</span>}
              </>
            )}
          </span>
          <span className="fd-pay-sub">
            {row.stamp}
            {row.seats > 1 && <> · {row.seats} entries</>}
            <span className="fd-pay-ord">{row.orderId}</span>
          </span>
        </span>

        <span className="fd-pay-rt">
          <span className="fd-pay-amt">{row.total}</span>
          <span className={`fd-pay-pill ${row.tone}`}>{row.statusLabel}</span>
        </span>

        <span className="fd-pay-ch" aria-hidden="true">
          <Icon name="expand_more" />
        </span>
      </button>

      {expanded && (
        <div className="fd-pay-exp">
          <div className="fd-pay-row">
            <span>Amount</span><b>{row.gross}</b>
          </div>
          {row.discount && (
            <div className="fd-pay-row"><span>Discount</span><b>− {row.discount}</b></div>
          )}
          {row.tax && (
            <div className="fd-pay-row"><span>Tax</span><b>{row.tax}</b></div>
          )}
          <div className="fd-pay-row tot">
            <span>{row.status === 'paid' ? 'Total paid' : 'Total'}</span>
            <span>{row.total}</span>
          </div>
          {row.refunded && (
            <div className="fd-pay-row"><span>Refunded</span><b className="ref">{row.refunded}</b></div>
          )}

          {row.note && <p className="fd-pay-note">{row.note}</p>}

          {/* {mailto && (
            <a className="fd-pay-mail" href={mailto}>
              <Icon name="mail" />Mail us about this payment
            </a>
          )} */}
          {onSupport && (
            <button
              type="button"
              className="fd-pay-mail"
              onClick={() => onSupport(row)}
            >
              <Icon name="mail" />Mail us about this payment
            </button>
          )}
        </div>
      )}
    </div>
  );
}
